import { motion } from "framer-motion";
import { Target, Users, TrendingUp, Award } from "lucide-react";

const teamMembers = [
  {
    name: "Wesley Ramjeet",
    role: "Founder & CEO",
    credentials: "CPA",
    description: "Wesley Ramjeet, CPA is an accomplished investor, serial entrepreneur, Founder & CEO of Unleashed Ventures, CEO of PPMT Strategic Group, CEO of MD Logic Health®, and Founding Member, Chairman of Lions Unleashed, a global 501(c)(3) nonprofit providing young immigrants and minorities with the tools and resources needed to excel post-graduation.",
    extendedBio: "With over two decades of experience navigating complex business landscapes—from IPOs and reverse mergers to M&A and venture capital—Wesley has built a career on transforming companies and driving exponential growth. He has successfully acquired, operationalized, and scaled multiple ventures across biotech, media, and wellness industries.",
    image: "https://customer-assets.emergentagent.com/job_9b5c43cb-efbb-4d52-9a1f-74b736257153/artifacts/3h6y57cv_wes%20ppmt%20headshot.png",
    featured: true,
  },
  {
    name: "Shanti Persaud",
    role: "Vice President",
    credentials: "RPh",
    description: "Strategic leadership and operational excellence supporting Unleashed Ventures' portfolio companies and investment initiatives.",
    image: "https://customer-assets.emergentagent.com/job_9b5c43cb-efbb-4d52-9a1f-74b736257153/artifacts/qxqs3rer_shanti.jpg",
  },
  {
    name: "Ashley Nicole",
    role: "Analyst",
    description: "Research and data-driven analysis supporting Unleashed Ventures' due diligence processes and portfolio performance tracking.",
    image: "https://customer-assets.emergentagent.com/job_9b5c43cb-efbb-4d52-9a1f-74b736257153/artifacts/ckjdq6wy_ashley%202.png",
  },
  {
    name: "Jyoti Hardat",
    role: "Technology & Innovation",
    description: "Driving technological advancement and digital transformation strategies across Unleashed Ventures' portfolio companies.",
    image: "https://customer-assets.emergentagent.com/job_9b5c43cb-efbb-4d52-9a1f-74b736257153/artifacts/npl6qapq_Jyoti.png",
  },
];

const values = [
  {
    icon: Target,
    title: "Precision",
    description: "Rigorous analysis and due diligence to identify exceptional opportunities.",
  },
  {
    icon: Users,
    title: "Partnership",
    description: "Active engagement with portfolio companies to drive value creation.",
  },
  {
    icon: TrendingUp,
    title: "Growth",
    description: "Long-term focus on sustainable growth and superior returns.",
  },
  {
    icon: Award,
    title: "Excellence",
    description: "Commitment to the highest standards of integrity and performance.",
  },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4"
              >
                About Us
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6"
              >
                Building
                <span className="block gold-gradient-text">Exceptional Value</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-slate-400 font-light text-base md:text-lg leading-relaxed mb-8"
              >
                Unleashed Ventures is a strategic investment firm focused on identifying 
                and nurturing high-potential micro-cap companies and private entities. 
                Our approach combines deep sector expertise with hands-on value creation 
                to deliver superior returns for our stakeholders.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="inline-flex items-center gap-3 border-l-2 border-amber-400/50 pl-4"
              >
                <span className="text-slate-500 text-sm font-light tracking-wide">Established</span>
                <span className="text-amber-400 text-2xl font-light">2019</span>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1769751641224-dbbda144e824?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Modern architecture representing corporate excellence"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-amber-400/30" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0F172A]/30" data-testid="mission-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4">
              Our Mission
            </p>
            <h2 className="text-3xl sm:text-4xl text-white mb-8">
              Unlocking Hidden Value in Tomorrow's Leaders
            </h2>
            <div className="space-y-6 text-slate-400 font-light leading-relaxed">
              <p>
                We believe exceptional investment opportunities often exist in overlooked 
                corners of the market. Our mission is to identify these opportunities 
                before they reach mainstream recognition, providing capital and strategic 
                support to help them realize their full potential.
              </p>
              <p>
                Through rigorous research, deep industry relationships, and active 
                portfolio management, we aim to generate superior risk-adjusted returns 
                while supporting innovative companies that are shaping the future of 
                healthcare, biotechnology, and emerging technologies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4">
            Our Values
          </p>
          <h2 className="text-3xl sm:text-4xl text-white mb-12">
            Guiding Principles
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8"
              >
                <div className="w-12 h-12 border border-amber-400/30 flex items-center justify-center mb-6">
                  <value.icon className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-white text-lg font-medium mb-3">{value.title}</h3>
                <p className="text-slate-500 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0F172A]/30" data-testid="team-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4">
            Our Team
          </p>
          <h2 className="text-3xl sm:text-4xl text-white mb-12">
            Leadership & Expertise
          </h2>

          {/* Featured Founder */}
          {teamMembers.filter(m => m.featured).map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                <div className="relative">
                  <div className="aspect-[4/5] max-w-md overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-amber-400/30" />
                </div>
                <div>
                  <span className="text-amber-400 text-xs uppercase tracking-[0.2em]">
                    {member.role}
                  </span>
                  <h3 className="text-white text-3xl sm:text-4xl mt-3 mb-6">
                    {member.name}
                    {member.credentials && (
                      <span className="text-slate-500 text-lg font-light ml-2">, {member.credentials}</span>
                    )}
                  </h3>
                  <p className="text-slate-300 font-light leading-relaxed mb-6">
                    {member.description}
                  </p>
                  {member.extendedBio && (
                    <p className="text-slate-500 font-light leading-relaxed">
                      {member.extendedBio}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}

          {/* Rest of Team */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.filter(m => !m.featured).map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="aspect-[4/5] overflow-hidden mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <span className="text-amber-400 text-xs uppercase tracking-wider">
                  {member.role}
                </span>
                <h3 className="text-white text-xl mt-2 mb-3">
                  {member.name}
                  {member.credentials && (
                    <span className="text-slate-500 text-sm font-light">, {member.credentials}</span>
                  )}
                </h3>
                <p className="text-slate-500 text-sm">{member.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Teams Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 pt-16 border-t border-white/10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="flex gap-4">
                <div className="w-1 bg-amber-400/50 flex-shrink-0" />
                <div>
                  <h4 className="text-white text-lg mb-3">Investment Team</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    Our dedicated investment team brings deep expertise in healthcare, biotechnology, 
                    and emerging technologies with rigorous due diligence processes to identify 
                    high-potential opportunities.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-1 bg-amber-400/50 flex-shrink-0" />
                <div>
                  <h4 className="text-white text-lg mb-3">Advisory Board</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    We are supported by industry veterans and domain experts who provide 
                    strategic guidance across our investment sectors, ensuring informed 
                    decision-making at every level.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "2019", label: "Established" },
              { value: "9+", label: "Portfolio Companies" },
              { value: "5+", label: "Industry Sectors" },
              { value: "100%", label: "Commitment" },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-amber-400 text-4xl md:text-5xl font-bold mb-2">
                  {stat.value}
                </p>
                <p className="text-slate-500 text-sm uppercase tracking-wider">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
